import axios from 'axios';

/** Сервис документов: с портала запросы идут через основной бэкенд (/api/v2/remains → :3003 → остатки 1С :3004) */
const BASE = '/api/v2/remains';

export type SearchMode = 'all' | 'barcode' | 'sku' | 'mnn' | 'analogs';
export type ViewScope = 'all' | 'assigned_pharmacies' | 'own_region';
export type ExpiryBucket = 'expired' | 'd30' | 'd90' | 'd180' | 'd365' | 'more';

/** Права оператора внутри раздела: без права поля нет в ответе сервера вовсе */
export interface RemainsPermissions {
  prices: boolean;
  /** Сумма остатка (кол-во × цена): сервер включает её только вместе с ценами */
  sums: boolean;
  contacts: boolean;
  kmDetails: boolean;
  export: boolean;
  refresh: boolean;
}

/** Состояние данных 1С (сервис номенклатуры): lastUpdated - ISO в UTC */
export interface RemainsDataStats {
  lastUpdated: string | null;
  lastDurationSec: number | null;
  totalItems: number;
  totalStockRecords: number;
  successPharmacies: number | string;
  errorPharmacies: number | string;
  status: 'ready' | 'empty' | 'error' | null;
  isLoading: boolean;
  progress: { done: number; total: number } | null;
}

/** Аптека зоны с координатами - точка отсчёта расстояния (в ней может и не быть искомого товара) */
export interface RemainsOrigin {
  guid: string;
  num: string | null;
  name: string;
  address: string | null;
  lat: number;
  lng: number;
}

/** «Моё место» - сохранённая геоточка самого оператора (профиль → «Геолокация»); savedAt - 'YYYY-MM-DD HH:mm' */
export interface RemainsSelf {
  lat: number;
  lng: number;
  /** Точность при сохранении, м; null - не известна */
  accuracy: number | null;
  address: string | null;
  savedAt: string | null;
}

export interface RemainsMeta {
  success: boolean;
  permissions: RemainsPermissions;
  scope: ViewScope;
  limits: { products: number; pharmacyProducts: number; pharmacyProductsMax: number; termMin: number };
  today: string;
  data: RemainsDataStats | null;
  dataError: string | null;
  origins: RemainsOrigin[];
  /** Регион аккаунта (users.cityId) для фильтра по умолчанию; null - не указан или в зоне нет его аптек */
  region: string | null;
  /** null - геоточка не сохранена (или справочник недоступен - тогда есть selfError) */
  self: RemainsSelf | null;
  selfError: string | null;
}

/** Ближайший срок годности товара в аптеке по журналу сроков (серии не учитываются) */
export interface StockExpiry {
  nearest: { date: string; qty: number; daysLeft: number; bucket: ExpiryBucket | null; bucketLabel: string | null } | null;
  expiredQty: number;
}

export interface RemainsPharmacy {
  guid: string;
  num: string | null;
  name: string;
  name1c: string | null;
  kind: 'pharmacy' | 'warehouse';
  region: string | null;
  address: string | null;
  lat: number | null;
  lng: number | null;
  hours: string | null;
  /** null - режим работы не указан или не распознан */
  open: boolean | null;
  closesAt: string | null;
  opensAt: string | null;
  /** Рабочий телефон аптеки - только при праве guide.remains.contacts */
  phone?: string | null;
  positions: number;
  units: number;
  /** Сумма по найденным товарам - только при праве guide.remains.sums */
  amount?: number;
}

export interface RemainsProduct {
  zid: string;
  sku: string | null;
  name: string;
  producer: string | null;
  mnn: string | null;
  category: string | null;
  barcode: string | null;
  folder: string | null;
  type: string | null;
  /** Делимость упаковки (> 1), иначе null */
  partCoeff: number | null;
  /** Данные отдела КМ - только при праве guide.remains.km_details */
  manager?: string | null;
  priceGroup?: string | null;
  notForMovements?: boolean;
  notForReturns?: boolean;
}

/** Остаток одного товара в одной аптеке */
export interface RemainsStock {
  guid: string;
  zid: string;
  qty: number;
  expiry: StockExpiry | null;
  /** Цена - при праве guide.remains.prices, сумма - при guide.remains.sums */
  price?: number;
  sum?: number;
}

export interface SearchQuery {
  term: string;
  mode: SearchMode;
  minQty: number;
}

export interface SearchResponse {
  success: boolean;
  term: string;
  mode: SearchMode;
  minQty: number;
  limit: number;
  today: string;
  time: string;
  truncated: boolean;
  products: RemainsProduct[];
  pharmacies: RemainsPharmacy[];
  stocks: RemainsStock[];
  warnings: string[];
}

/** Товар в карточке аптеки: карточка товара + остаток в этой аптеке */
export interface PharmacyProduct extends RemainsProduct {
  qty: number;
  expiry: StockExpiry | null;
  price?: number;
  sum?: number;
}

export interface PharmacyResponse {
  success: boolean;
  today: string;
  pharmacy: RemainsPharmacy;
  products: PharmacyProduct[];
  truncated: boolean;
  limit: number;
  warnings: string[];
}

/** Фильтры списка аптек, которые сервер повторяет в выгрузке */
export interface ExportFilters {
  region: string;
  openNow: boolean;
  missing: '' | 'hours' | 'phone';
  /** Выбранные товары из найденных (пусто - все) */
  zids: string[];
  /** Точка отсчёта и радиус, км: без любой из частей фильтр по расстоянию не применяется */
  near: { lat: number; lng: number } | null;
  radius: number | null;
}

export interface DownloadedFile { blob: Blob; filename: string }

/** Повторная загрузка того же запроса («Обновить», «Повторить») в журнал действий не пишется */
const silentParam = (silent: boolean): Record<string, string> => (silent ? { silent: '1' } : {});

const queryParams = (q: SearchQuery): Record<string, string> => ({
  term: q.term,
  mode: q.mode,
  ...(q.minQty > 0 ? { minQty: String(q.minQty) } : {})
});

/** Координаты с точностью ~100 м: этого хватает для радиуса, а точное местоположение оператора не уходит на сервер */
const coord = (v: number): string => v.toFixed(3);

function exportParams(q: SearchQuery, f: ExportFilters): Record<string, string> {
  const p = queryParams(q);
  if (f.region) p.region = f.region;
  if (f.openNow) p.open = '1';
  if (f.missing) p.missing = f.missing;
  if (f.zids.length) p.zids = f.zids.join(',');
  if (f.near && f.radius) {
    p.near = `${coord(f.near.lat)},${coord(f.near.lng)}`;
    p.radius = String(f.radius);
  }
  return p;
}

/** Имя файла из Content-Disposition: сначала русское (filename*=UTF-8''…), затем ASCII (filename="…") */
function fileNameFrom(disposition: unknown): string {
  const header = String(disposition || '');
  const utf = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf) {
    try {
      return decodeURIComponent(utf[1].trim());
    } catch {
      // некорректная кодировка - берём ASCII-имя
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  if (plain) return plain[1];
  return `remains_${new Date().toISOString().slice(0, 10)}.xlsx`;
}

export const remainsApi = {
  async meta(signal?: AbortSignal): Promise<RemainsMeta> {
    const res = await axios.get(`${BASE}/meta`, { signal });
    return res.data;
  },

  async search(q: SearchQuery, silent: boolean, signal?: AbortSignal): Promise<SearchResponse> {
    const res = await axios.get(`${BASE}/search`, { params: { ...queryParams(q), ...silentParam(silent) }, signal });
    return res.data;
  },

  /** Карточка аптеки: zids - найденные товары; без них - весь ассортимент (term - поиск по нему) */
  async pharmacy(guid: string, opts: { zids?: string[]; term?: string; limit?: number }, silent: boolean, signal?: AbortSignal): Promise<PharmacyResponse> {
    const params: Record<string, string> = { ...silentParam(silent) };
    if (opts.zids && opts.zids.length) params.zids = opts.zids.join(',');
    if (opts.term) params.term = opts.term;
    if (opts.limit) params.limit = String(opts.limit);
    const res = await axios.get(`${BASE}/pharmacy/${encodeURIComponent(guid)}`, { params, signal });
    return res.data;
  },

  /** Excel по текущему запросу и фильтрам экрана: колонки без права сервер не добавляет */
  async exportFile(q: SearchQuery, f: ExportFilters, signal?: AbortSignal): Promise<DownloadedFile> {
    const res = await axios.get(`${BASE}/export`, {
      params: exportParams(q, f),
      responseType: 'blob',
      // файл может собираться дольше общего таймаута axios, обрыв - через signal
      timeout: 0,
      signal
    });
    return { blob: res.data, filename: fileNameFrom(res.headers['content-disposition']) };
  },

  async refresh(): Promise<{ started: boolean; message: string }> {
    const res = await axios.post(`${BASE}/refresh`, {});
    return res.data;
  }
};

export const isCancelled = (err: unknown): boolean => axios.isCancel(err);

/** 403 - нет права на раздел, действие или аптека вне зоны */
export const isForbidden = (err: unknown): boolean => (err as { response?: { status?: number } })?.response?.status === 403;
